import { useCallback, useState } from 'react';
import { Button } from '@mui/material';
import { useAtom } from 'jotai';
import { useDocumentList } from 'shared/_components/list/use-document-list';
import { DocType } from 'shared/constants/docs';
import ProjectListItem from 'shared/_model/project/project-list/project-list-item';
import { ListAtomValue } from 'shared/_components/list/types';
import { TDocProject } from 'shared/model/project';
import { TDocument } from 'shared/model/doc-base';
import ProjectListFilters from 'shared/_model/project/project-list/project-list-filters';
import ProjectCreate from 'shared/_model/project/project-create';
import { getProjectTotals } from 'shared/_model/project/project-list/project-list-item/utils';
import ProjectListActionsLeft from 'shared/_model/project/project-list/project-list-actions-left';
import ProjectListActionsRight from 'shared/_model/project/project-list/project-list-actions-right';
import { projectListAtom, projectListSettingsAtom } from 'shared/_model/project/project-list/store';
import { Page, PageContentList } from 'shared/_components/page/styles';
import EmptyState from 'shared/_components/empty-state';
import PageListHeader from 'shared/_components/page/page-list-header';

export default function ProjectList() {
	const [{ items, total }, setList] = useAtom(projectListAtom);
	const [showCreate, setShowCreate] = useState(false);
	const toggleCreateNewProject = useCallback(() => {
		setShowCreate((s) => !s);
	}, []);

	const handleRefresh = useCallback(
		async (value: ListAtomValue<TDocProject>, db: PouchDB.Database<TDocument>) => {
			if (!db) {
				return;
			}
			const totals = await getProjectTotals(
				value.items.map((it) => it._id),
				db,
			);

			setList({
				...value,
				items: value.items.map((it) => ({
					...it,
					_totals: {
						fileCount: totals[it._id],
					},
				})),
			});
		},
		[setList],
	);

	const { refresh, loaded } = useDocumentList({
		type: DocType.PROJECT,
		listAtom: projectListAtom,
		settingsAtom: projectListSettingsAtom,
		onRefresh: handleRefresh,
		doSetList: false,
	});

	// Add is shown in the header only for a loaded, non-empty list; an empty list has it in the empty state.
	const showAdd = loaded && total > 0;

	return (
		<Page>
			<PageListHeader
				count={total}
				actionsLeft={<ProjectListActionsLeft refresh={refresh} />}
				actionsRight={<ProjectListActionsRight toggleCreateNewProject={toggleCreateNewProject} showAdd={showAdd} />}
			>
				Projects
			</PageListHeader>
			<ProjectListFilters />
			{loaded && total === 0 && (
				<EmptyState
					title="No projects yet"
					description="Create your first project to start adding videos and audio."
					action={<Button onClick={toggleCreateNewProject}>Add Project</Button>}
				/>
			)}
			<PageContentList>
				{items.map((item) => (
					<ProjectListItem key={item._id} {...item} />
				))}
			</PageContentList>
			{showCreate && <ProjectCreate onClose={toggleCreateNewProject} />}
		</Page>
	);
}
